import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../utils/constants';

function userInitial(email?: string): string {
  return email ? email[0].toUpperCase() : '?';
}

// Avatar dropdown (Settings + Sign out). Shared by the global Header (all
// non-project pages) and the project workspace's own consolidated header
// row, so the dropdown/outside-click/logout behavior lives in one place.
export function AccountMenu() {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate(ROUTES.LOGIN);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-8 h-8 rounded-full bg-accent-600 text-white text-sm font-semibold flex items-center justify-center hover:bg-accent-700 transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
        aria-label="Account menu"
      >
        {userInitial(user?.email)}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 z-50">
          <Link
            to={ROUTES.SETTINGS}
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            {t('settings')}
          </Link>
          <div className="border-t border-neutral-100 my-1" />
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            {t('logout')}
          </button>
        </div>
      )}
    </div>
  );
}
